import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MANDANT_TYPE_LABELS, MandantTypeSchema, type MandantDto } from '../../lib/api-types';
import type { MandantCreateInput } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { FieldHelp } from '../../components/ui/FieldHelp';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';

const mandantFormSchema = z.object({
  nom: z.string().min(1, 'Le nom est requis.'),
  type: MandantTypeSchema,
  telephone: z.string().optional(),
  email: z.string().email('Adresse e-mail invalide.').optional().or(z.literal('')),
  adresse: z.string().optional(),
  contact: z.string().optional(),
});
type MandantFormValues = z.infer<typeof mandantFormSchema>;

/** Clearly fictitious demo values — sample labels only, never real contacts. */
const DEMO_MANDANT: MandantFormValues = {
  nom: 'Banque de Démonstration (ex. : à modifier)',
  type: 'BANQUE',
  telephone: '+226 25 00 00 00',
  email: 'contact@demo.example',
  adresse: 'Ouagadougou — Ouaga 2000',
  contact: 'M. Demo Exemple',
};

export function MandantForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  pending,
}: {
  initial?: MandantDto;
  submitLabel: string;
  onSubmit: (input: MandantCreateInput) => void;
  onCancel?: () => void;
  pending?: boolean;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<MandantFormValues>({
    resolver: zodResolver(mandantFormSchema),
    defaultValues: {
      nom: initial?.nom ?? '',
      type: initial?.type ?? 'PARTICULIER',
      telephone: initial?.coordonnees?.telephone ?? '',
      email: initial?.coordonnees?.email ?? '',
      adresse: initial?.coordonnees?.adresse ?? '',
      contact: initial?.coordonnees?.contact ?? '',
    },
  });

  const [confirmDemo, setConfirmDemo] = useState(false);

  const applyDemo = () => {
    // Prefill coherent demo values client-side; the user can edit them after.
    (Object.keys(DEMO_MANDANT) as (keyof MandantFormValues)[]).forEach((key) => {
      setValue(key, DEMO_MANDANT[key], { shouldValidate: false });
    });
  };

  const requestDemo = () => {
    const values = getValues();
    const hasData = (['nom', 'telephone', 'email', 'adresse', 'contact'] as const).some(
      (key) => (values[key] ?? '').trim().length > 0,
    );
    if (hasData) {
      setConfirmDemo(true);
      return;
    }
    applyDemo();
  };

  const submit = handleSubmit((values) => {
    const coordonnees: MandantCreateInput['coordonnees'] = {};
    if (values.telephone) coordonnees.telephone = values.telephone;
    if (values.email) coordonnees.email = values.email;
    if (values.adresse) coordonnees.adresse = values.adresse;
    if (values.contact) coordonnees.contact = values.contact;
    onSubmit({
      nom: values.nom,
      type: values.type,
      coordonnees: Object.keys(coordonnees).length > 0 ? coordonnees : undefined,
    });
  });

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {/* The demo prefill is a create-time convenience; hidden when editing. */}
      {initial ? null : (
        <div className="rounded-md border border-dashed border-brand-200 bg-brand-50/50 px-3 py-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={requestDemo}
            disabled={pending}
          >
            Utiliser un exemple de démonstration
          </Button>
          <FieldHelp className="mt-1">
            Remplit le formulaire avec des valeurs d’exemple, que vous pouvez ensuite modifier.
          </FieldHelp>
        </div>
      )}

      <div>
        <Label htmlFor="nom">Nom</Label>
        <Input
          id="nom"
          placeholder="ex. : Banque de Démonstration"
          aria-invalid={Boolean(errors.nom)}
          {...register('nom')}
        />
        {errors.nom ? <p className="mt-1 text-sm text-red-600">{errors.nom.message}</p> : null}
        <FieldHelp>Le nom du client qui commande l’évaluation.</FieldHelp>
      </div>

      <div>
        <Label htmlFor="type">Type</Label>
        <Select id="type" {...register('type')}>
          {MandantTypeSchema.options.map((t) => (
            <option key={t} value={t}>
              {MANDANT_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <FieldHelp>La catégorie du mandant (banque, assurance, particulier…).</FieldHelp>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="telephone">Téléphone</Label>
          <Input id="telephone" placeholder="ex. : +226 25 00 00 00" {...register('telephone')} />
        </div>
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            placeholder="ex. : contact@demo.example"
            {...register('email')}
          />
          {errors.email ? (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          ) : null}
        </div>
      </div>

      <div>
        <Label htmlFor="adresse">Adresse</Label>
        <Input id="adresse" placeholder="ex. : Ouagadougou — Ouaga 2000" {...register('adresse')} />
      </div>

      <div>
        <Label htmlFor="contact">Contact</Label>
        <Input id="contact" placeholder="ex. : M. Demo Exemple" {...register('contact')} />
        <FieldHelp>La personne à contacter chez le mandant (facultatif).</FieldHelp>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Annuler
          </Button>
        ) : null}
        <Button type="submit" loading={pending}>
          {submitLabel}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDemo}
        onClose={() => setConfirmDemo(false)}
        onConfirm={() => {
          applyDemo();
          setConfirmDemo(false);
        }}
        title="Remplacer par l’exemple de démonstration ?"
        description="Des informations ont déjà été saisies. Voulez-vous les remplacer par les valeurs d’exemple ? Vous pourrez encore les modifier ensuite."
        confirmLabel="Remplacer"
      />
    </form>
  );
}
